import { useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { DocumentParserContext } from '../contexts/documentParserProvider';
import { documentParserAPI } from '../api/documentParser';

export const useDocumentParser = () => {
  const { state, actions } = useContext(DocumentParserContext);
  const { t } = useTranslation();

  const {
    setLoading,
    setError,
    setProjects,
    setCurrentProject,
    setSchemas,
    setStatistics,
    setMonitoringData,
    addProject,
    updateProject,
    deleteProject,
    clearError,
  } = actions;

  // Fetch all projects
  const fetchProjects = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      clearError();
      const response = await documentParserAPI.getAllProjects();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (!silent) {
        setError(t('documentParser.errors.fetchProjects'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [setLoading, setProjects, setError, clearError, t]);

  // Fetch project by ID
  const fetchProjectById = useCallback(async (id, silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      clearError();
      const response = await documentParserAPI.getProjectById(id);
      setCurrentProject(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching project:', error);
      if (!silent) {
        setError(t('documentParser.errors.fetchProject'));
      }
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [setLoading, setCurrentProject, setError, clearError, t]);

  // Create new project
  const createProject = useCallback(async (projectData) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.createProject(projectData);
      addProject(response.data);
      
      // 排程配置交由後端處理
      // 後端會根據 scheduleConfig 自動創建或更新排程
      if (projectData.config?.scheduleConfig?.type === 'scheduled') {
        console.log('✅ 項目設定為排程模式，後端將自動處理排程創建');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating project:', error);
      setError(t('documentParser.errors.createProject'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, addProject, setError, clearError, t]);

  // Update project
  const updateProjectData = useCallback(async (id, projectData) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.updateProject(id, projectData);
      
      // 更新個別專案狀態
      updateProject(response.data);
      
      // 🔥 移除延遲刷新，避免重複專案問題
      // 相信我們已經修復的資料格式標準化，不需要額外的防禦性刷新
      
      // 排程配置交由後端處理
      // 後端會根據 scheduleConfig 自動創建或更新排程
      if (projectData.config?.scheduleConfig?.type === 'scheduled') {
        console.log('✅ 項目更新為排程模式，後端將自動處理排程管理');
      }
      
      return response.data;
    } catch (error) {
      console.error('Error updating project:', error);
      setError(t('documentParser.errors.updateProject'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, updateProject, setError, clearError, t]);

  // Delete project
  const deleteProjectData = useCallback(async (id) => {
    try {
      setLoading(true);
      clearError();
      await documentParserAPI.deleteProject(id);
      deleteProject(id);
    } catch (error) {
      console.error('Error deleting project:', error);
      setError(t('documentParser.errors.deleteProject'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, deleteProject, setError, clearError, t]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.getStatistics();
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      setError(t('documentParser.errors.fetchStatistics'));
    } finally {
      setLoading(false);
    }
  }, [setLoading, setStatistics, setError, clearError, t]);

  // Fetch monitoring data
  const fetchMonitoringData = useCallback(async (projectId, silent = false) => {
    try {
      // silent 模式下不設置 loading，避免 UI 閃爍
      if (!silent) {
        setLoading(true);
      }
      clearError();
      const response = await documentParserAPI.getMonitoringData(projectId);
      setMonitoringData(response.data);
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      if (!silent) {
        setError(t('documentParser.errors.fetchMonitoringData'));
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [setLoading, setMonitoringData, setError, clearError, t]);

  // Calculate real-time statistics from projects
  const calculateStatistics = useCallback((projects) => {
    if (!projects || projects.length === 0) {
      return {
        totalProjects: 0,
        activeProjects: 0,
        totalDocuments: 0,
        successRate: 0,
        recentActivities: [],
        performanceMetrics: {
          avgProcessingTime: 0,
          avgAccuracy: 0,
          totalErrors: 0
        }
      };
    }

    const activeProjects = projects.filter(p => p.status === 'active' || p.status === 'monitoring');
    const totalDocuments = projects.reduce((sum, p) => sum + (p.stats?.processedDocuments || 0), 0);
    const totalSuccessful = projects.reduce((sum, p) => sum + (p.stats?.successfulProcessing || 0), 0);
    const successRate = totalDocuments > 0 ? Math.round((totalSuccessful / totalDocuments) * 100) : 0;

    // 計算最近活動
    const recentActivities = projects
      .filter(p => p.lastActivity)
      .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
      .slice(0, 10)
      .map(p => ({
        id: p.id,
        name: p.name,
        action: p.status === 'active' ? '正在處理' : p.status === 'completed' ? '處理完成' : p.status === 'monitoring' ? '監控中' : '暫停',
        timestamp: p.lastActivity,
        status: p.status
      }));

    // 計算效能指標
    const performanceMetrics = {
      avgProcessingTime: Math.round(
        projects.reduce((sum, p) => sum + (p.stats?.avgProcessingTime || 0), 0) / projects.length
      ),
      avgAccuracy: Math.round(
        projects.reduce((sum, p) => sum + (p.stats?.accuracy || 0), 0) / projects.length
      ),
      totalErrors: projects.reduce((sum, p) => sum + (p.stats?.errors || 0), 0)
    };

    return {
      totalProjects: projects.length,
      activeProjects: activeProjects.length,
      totalDocuments,
      successRate,
      recentActivities,
      performanceMetrics
    };
  }, []);

  // Auto-update statistics when projects change
  const updateStatistics = useCallback(() => {
    const calculatedStats = calculateStatistics(state.projects);
    setStatistics(calculatedStats);
    return calculatedStats;
  }, [state.projects, calculateStatistics, setStatistics]);

  // Toggle project status
  const toggleProjectStatus = useCallback(async (id, newStatus) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.toggleProjectStatus(id, newStatus);
      updateProject(response.data);
      message.success(t('documentParser.statusToggled'));
      return response.data;
    } catch (error) {
      console.error('Error toggling project status:', error);
      setError(t('documentParser.errors.toggleStatus'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, updateProject, setError, clearError, t]);

  // Manual folder processing
  const processProjectFolder = useCallback(async (id, options = {}) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.processProjectFolder(id, options);
      message.success(t('documentParser.monitor.messages.processingStarted') || '已開始處理資料夾');
      return response.data;
    } catch (error) {
      console.error('Error processing folder:', error);
      setError(t('documentParser.errors.processFolder') || '處理資料夾失敗');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  // Validate schema
  const validateSchema = useCallback(async (schema) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.validateSchema(schema);
      return response.data;
    } catch (error) {
      console.error('Error validating schema:', error);
      setError(t('documentParser.errors.validateSchema'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  // Test processing
  const testProcessing = useCallback(async (projectId, file) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.testProcessing(projectId, file);
      message.success(t('documentParser.testProcessingSuccess'));
      return response.data;
    } catch (error) {
      console.error('Error testing processing:', error);
      setError(t('documentParser.errors.testProcessing'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);



  // Schema CRUD operations
  const fetchSchemas = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.getAllSchemas();
      setSchemas(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching schemas:', error);
      setError(t('documentParser.errors.fetchSchemas'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setSchemas, clearError, t]);

  const fetchSchemaById = useCallback(async (id) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.getSchemaById(id);
      return response.data;
    } catch (error) {
      console.error('Error fetching schema:', error);
      setError(t('documentParser.errors.fetchSchema'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  const createSchema = useCallback(async (schemaData) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.createSchema(schemaData);
      return response.data;
    } catch (error) {
      console.error('Error creating schema:', error);
      setError(t('documentParser.errors.createSchema'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  const updateSchema = useCallback(async (id, schemaData) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.updateSchema(id, schemaData);
      return response.data;
    } catch (error) {
      console.error('Error updating schema:', error);
      setError(t('documentParser.errors.updateSchema'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  const deleteSchema = useCallback(async (id) => {
    try {
      setLoading(true);
      clearError();
      await documentParserAPI.deleteSchema(id);
    } catch (error) {
      console.error('Error deleting schema:', error);
      setError(t('documentParser.errors.deleteSchema'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  const fetchSchemaStatistics = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.getSchemaStatistics();
      return response.data;
    } catch (error) {
      console.error('Error fetching schema statistics:', error);
      setError(t('documentParser.errors.fetchSchemaStatistics'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, t]);

  // Project status monitoring
  const monitorProjectStatus = useCallback(async (projectId, onStatusChange) => {
    const checkStatus = async () => {
      try {
        const project = await fetchProjectById(projectId, true);
        if (project && onStatusChange) {
          onStatusChange(project);
        }
        return project;
      } catch (error) {
        console.error('Error monitoring project status:', error);
        return null;
      }
    };

    return checkStatus;
  }, [fetchProjectById]);

  // Monitor multiple AI generating projects
  const monitorAIGeneratingProjects = useCallback(() => {
    const aiGeneratingProjects = state.projects.filter(
      project => project.project_status === 'AI_GENERATING' || project.project_status === 'ai_generating'
    );

    if (aiGeneratingProjects.length === 0) return null;

    const intervalId = setInterval(async () => {
      try {
        const updatedProjects = await Promise.all(
          aiGeneratingProjects.map(async (project) => {
            const updated = await fetchProjectById(project.document_project_id, true);
            return updated;
          })
        );

        // Check if any project has completed AI generation
        const completedProjects = updatedProjects.filter(
          (project, index) => {
            const originalStatus = aiGeneratingProjects[index].project_status;
            const newStatus = project?.project_status;
            return (originalStatus === 'AI_GENERATING' || originalStatus === 'ai_generating') &&
                   newStatus && newStatus !== 'AI_GENERATING' && newStatus !== 'ai_generating';
          }
        );

        if (completedProjects.length > 0) {
          // Refresh the full project list
          await fetchProjects();
          
          // Show completion messages
          completedProjects.forEach(project => {
            if (project.project_status === 'configuring') {
              message.success(t('documentParser.ai.generationCompleted', { name: project.project_name }));
            }
          });
        }
      } catch (error) {
        console.error('Error monitoring AI generating projects:', error);
      }
    }, 10000); // Check every 10 seconds

    return intervalId;
  }, [state.projects, fetchProjectById, fetchProjects, t]);

  // AI Schema Generation Recovery
  const retryAIGeneration = useCallback(async (projectId) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.retryAIGeneration(projectId);
      message.success(t('documentParser.recovery.retrySuccess'));
      // Refresh projects list to show updated status
      await fetchProjects();
      return response;
    } catch (error) {
      console.error('Error retrying AI generation:', error);
      setError(t('documentParser.recovery.retryError'));
      message.error(t('documentParser.recovery.retryError'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, fetchProjects, t]);

  const resetToManual = useCallback(async (projectId) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.resetToManual(projectId);
      message.success(t('documentParser.recovery.resetSuccess'));
      // Refresh projects list to show updated status
      await fetchProjects();
      return response;
    } catch (error) {
      console.error('Error resetting to manual:', error);
      setError(t('documentParser.recovery.resetError'));
      message.error(t('documentParser.recovery.resetError'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, fetchProjects, t]);

  // Processing Status and Stage Details
  const getCeleryTaskStatus = useCallback(async (taskId) => {
    try {
      const response = await documentParserAPI.getCeleryTaskStatus(taskId);
      return response.data;
    } catch (error) {
      console.error('Error getting celery task status:', error);
      throw error;
    }
  }, []);

  const getProjectProcessingStatus = useCallback(async (projectId) => {
    try {
      const response = await documentParserAPI.getProjectProcessingStatus(projectId);
      return response.data;
    } catch (error) {
      console.error('Error getting project processing status:', error);
      throw error;
    }
  }, []);

  const terminateProjectProcessing = useCallback(async (projectId) => {
    try {
      const response = await documentParserAPI.terminateProjectProcessing(projectId);
      return response.data;
    } catch (error) {
      console.error('Error terminating project processing:', error);
      throw error;
    }
  }, []);

  const deleteProjectTasks = useCallback(async (projectId, statuses = null) => {
    try {
      setLoading(true);
      clearError();
      const response = await documentParserAPI.deleteProjectTasks(projectId, statuses);
      
      // 刪除任務後，重新獲取任務列表和監控資料
      await fetchMonitoringData(projectId);
      
      return response.data;
    } catch (error) {
      console.error('Error deleting project tasks:', error);
      setError(t('documentParser.errors.deleteTasksFailed'));
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, clearError, fetchMonitoringData, t]);

  const deleteSingleTask = useCallback(async (projectId, taskId) => {
    try {
      const response = await documentParserAPI.deleteSingleTask(projectId, taskId);
      return response.data;
    } catch (error) {
      console.error('Error deleting single task:', error);
      throw error;
    }
  }, []);

  // Return hook interface
  return {
    // State
    projects: state.projects,
    currentProject: state.currentProject,
    statistics: state.statistics,
    monitoringData: state.monitoringData,
    loading: state.loading,
    error: state.error,

    // Actions
    fetchProjects,
    fetchProjectById,
    createProject,
    updateProject: updateProjectData,
    deleteProject: deleteProjectData,
    fetchStatistics,
    fetchMonitoringData,
    toggleProjectStatus,
    processProjectFolder,
    validateSchema,
    testProcessing,
    clearError,

    // Schema operations
    schemas: state.schemas || [],
    fetchSchemas,
    fetchSchemaById,
    createSchema,
    updateSchema,
    deleteSchema,
    fetchSchemaStatistics,

    // New optimized statistics functions
    calculateStatistics,
    updateStatistics,
    
    // Project status monitoring
    monitorProjectStatus,
    monitorAIGeneratingProjects,
    
    // AI Schema Generation Recovery
    retryAIGeneration,
    resetToManual,
    
    // Processing Status and Stage Details
    getCeleryTaskStatus,
    getProjectProcessingStatus,
    terminateProjectProcessing,
    deleteProjectTasks,
    deleteSingleTask,
  };
};

export default useDocumentParser;